import React, { useState, memo, useMemo, useEffect } from "react";
import L from "leaflet";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-routing-machine";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/assets/css/leaflet.css";
import LocationSVG from "../icons/LocationSVG";
import { Marker, Popup } from "react-leaflet";
import { IoLocationSharp } from "react-icons/io5";
import { ImHome } from "react-icons/im";
import { 
  createCategoryIcon, 
  createImgIcon, 
  createUserIcon,
  onIconReady 
} from "../../util/mapStyles";
import { getKnownServiceImg } from "../../util/iconRegistry";
import { useMapContext } from "../../context/MapContext/MapContext";
import { useData } from "../../context/DataContext/DataProvider";

export const MarkerPopupButton = ({ Icon, iconsize = 12,text, onClick }) => {
  return (
    <div
      className="flex items-center justify-start cursor-pointer px-kit-12 py-kit-8 rounded-kit-xs w-full hover:text-[--inyc-primary-text-color] transition-all duration-300 hover:bg-[--inyc-primary-light] text-[--inyc-primary-text-color] text-kit-base"
      style={{ width: '100%' }}
      onClick={onClick}
    >
      <Icon
        className="mr-kit-8"
        size={12}
      />
      <div
        className="text-kit-base leading-[1.5rem] text-[--inyc-primary-text-color]"
      >
        {text}
      </div>
    </div>
  )
}

function MapMarker({ zIndexOffset, data, Popup, onClick, noHover = false, icon, position, ignoreNextMapClickRef, event, clickHandler, selected, smallIcon, tagFilter }) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [iconVersion, setIconVersion] = useState(0); // Force re-render when icons are ready
  
  // smallIcon is now passed as a prop from Map component (which manages zoom state)
  // tagFilter is now passed as a prop to ensure memo comparison works correctly
  const { globalConfig } = useData();
  const knownServices = globalConfig?.knownServices || {};
  
  // Subscribe to icon ready events to force re-render when async icons are generated
  useEffect(() => {
    const unsubscribe = onIconReady(() => {
      setIconVersion(v => v + 1);
    });
    return unsubscribe;
  }, []);

  // Calculate icon internally if not provided
  const calculatedIcon = useMemo(() => {
    // If icon is explicitly provided, use it
    if (icon) {
      return icon;
    }

    // If no event data, return default icon
    if (!event) {
      return createUserIcon();
    }

    // Check if event has a tag from knownServices
    const hasKnownServiceTag = [
      ...(event.tags || []),
      ...(event._matchingEvent?.tags || []),
    ]?.some(
      (tag) =>
        (knownServices?.[tag?.toLowerCase()]?.icon ?? false) &&
        tagFilter?.includes(tag?.toLowerCase())
    );

    if (hasKnownServiceTag) {
      return createImgIcon(
        getKnownServiceImg(
          knownServices?.[
            [
              ...(event.tags || []),
              ...(event._matchingEvent?.tags || []),
            ]
              ?.find(
                (tag) =>
                  (knownServices?.[tag?.toLowerCase()]?.icon ?? false) &&
                  tagFilter?.includes(tag?.toLowerCase())
              )
              ?.toLowerCase()
          ]?.icon
        ),
        event.events?.length
      );
    }

    // Use the selected prop to determine if this marker is selected
    if (selected) {
      return createCategoryIcon(
        event.category,
        "darkred",
        event.events?.length,
        smallIcon
      ) || createUserIcon();
    }

    // Default icon for unselected markers
    return createCategoryIcon(
      event.category || event._matchingEvent?.category,
      null,
      event.events?.length,
      smallIcon
    ) || createUserIcon();
  }, [
    icon,
    event,
    knownServices,
    tagFilter,
    smallIcon,
    selected,
    iconVersion // Re-calculate when async icons become ready
  ]);

  // Construct onClick handler internally to avoid re-renders
  const handleClick = (e) => {
    if (Popup) {
      setIsPopupOpen(true);
      ignoreNextMapClickRef.current = true;
    } else {
      // Use the passed onClick if available, otherwise use the event-based handler
      if (onClick) {
        onClick(e);
      } else if (clickHandler){
        clickHandler(event);
      } else if (onMarkerClick && event) {
        onMarkerClick(event);
      }
    }
  };

  const eventCoordinates = event?.location?.coordinates;
  const hasEventPosition =
    Array.isArray(eventCoordinates) &&
    eventCoordinates.length >= 2 &&
    Number.isFinite(Number(eventCoordinates[0])) &&
    Number.isFinite(Number(eventCoordinates[1]));

  if (!position && !hasEventPosition) {
    return null;
  }

  const cposition = position || [eventCoordinates[1], eventCoordinates[0]];

  return (
    <Marker
      zIndexOffset={zIndexOffset}
      icon={calculatedIcon}
      position={cposition}
      eventHandlers={{
        click: handleClick
      }}
    >
      {isPopupOpen &&
        <Popup
          onClose={() => {
            // ignoreNextMapClickRef.current = true;
            setIsPopupOpen(false);
          }}
          onOpen={() => setIsPopupOpen(true)}
          eventHandlers={{
            click: (e) => {
              e?.stopPropagation();
              e?.preventDefault();
            }
          }}
          {...data?.props}
        />
      }
    </Marker>
  );
}

// Memoize MapMarker to prevent unnecessary re-renders
const MemoizedMapMarker = memo(MapMarker, (prevProps, nextProps) => {
  // Only re-render if essential props change
  return (
    prevProps.selected === nextProps.selected &&
    prevProps.icon === nextProps.icon &&
    prevProps.position === nextProps.position &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.onMarkerClick === nextProps.onMarkerClick &&
    prevProps.event === nextProps.event &&
    prevProps.title === nextProps.title &&
    prevProps.zIndexOffset === nextProps.zIndexOffset &&
    prevProps.smallIcon === nextProps.smallIcon &&
    prevProps.tagFilter === nextProps.tagFilter
  );
});

export default MemoizedMapMarker;
